<template>
    <div class="squadTable table_sort">
        <!-- Кого с картинки не нашли в ростере. Клик убирает плашку -->
        <div v-if="missedNames.length" class="ocr-missed screenshot-hidden">
            <!-- Текст выделяемый: имена копируют руками. Убирается только крестиком,
                 иначе плашка исчезала на первом же клике при выделении -->
            <span class="ocr-missed-text">Нет в составе: {{ missedNames.join(', ') }}</span>
            <button type="button" class="ocr-missed-close" title="Убрать" @click="missedNames = []">×</button>
        </div>
        <div class="captions">
            <div class="teamname">
                <!-- Эмблема вынесена из ссылки на ТМ: по ней теперь открывается окошко
                     с сайтом и соцсетями клуба, а по названию по-прежнему уходим на ТМ -->
                <span class="team-logo" @click.stop="toggleTeamLinks">
                    <img :src="team.img_href" alt="teamlogo" :title="`Ссылки клуба: ${teamName}`" />

                    <!-- Обработчик обязателен: во Vue 2 «@click.stop» без выражения не вешается,
                         клик долетал до эмблемы и заново открывал только что закрытое окно -->
                    <div class="team-links" v-if="teamLinksShow" @click.stop="stopClick">
                        <div class="team-links-head">
                            <span class="team-links-name">{{ teamName }}</span>
                            <button type="button" class="team-links-refresh" title="Обновить ссылки"
                                    :disabled="teamLinksLoading" @click.stop="reloadTeamLinks">
                                <svg viewBox="0 0 24 24" width="17" height="17">
                                    <circle cx="12" cy="12" r="12" fill="#2196f3"/>
                                    <path fill="#fff" d="M12 5.5c2.02 0 3.79 1.05 4.8 2.64l1.45-1.45v4.4h-4.4l1.68-1.68A4.28 4.28 0 0 0 12 7.6a4.4 4.4 0 0 0-4.24 3.25l-1.9-.52A6.36 6.36 0 0 1 12 5.5zm0 13c-2.02 0-3.79-1.05-4.8-2.64L5.75 17.3v-4.4h4.4L8.47 14.6A4.28 4.28 0 0 0 12 16.4a4.4 4.4 0 0 0 4.24-3.25l1.9.52A6.36 6.36 0 0 1 12 18.5z"/>
                                </svg>
                            </button>
                            <button type="button" class="team-links-close" title="Закрыть"
                                    @click.stop="teamLinksShow = false">×</button>
                        </div>

                        <div class="team-links-empty" v-if="teamLinksLoading">Ищем…</div>
                        <div class="team-links-empty" v-else-if="!teamLinksList.length">
                            Ничего не нашли —
                            <a :href="`https://www.google.com/search?q=${encodeURIComponent(teamName + ' official site')}`"
                               target="_blank">поискать вручную</a>
                        </div>
                        <!-- Пока фоновый поиск соцсетей не закончился, говорим об этом -->
                        <div class="team-links-empty" v-if="teamLinksPending && !teamLinksLoading">
                            Ищем соцсети…
                        </div>

                        <div class="team-links-row" v-for="link in teamLinksList" :key="link.key">
                            <template v-if="teamLinkEditing !== link.key">
                                <a :href="link.url" target="_blank"
                                   :class="{ 'team-links-search': link.search, 'team-links-manual': link.manual }">
                                    <span class="team-links-ico" v-if="link.icon" v-html="link.icon"></span>
                                    {{ link.label }}
                                </a>
                                <!-- Карандаш: вписать ссылку руками, если поиск не нашёл или ошибся -->
                                <button type="button" class="team-links-edit" title="Вписать ссылку вручную"
                                        @click.stop="startEditLink(link)">✎</button>
                            </template>

                            <template v-else>
                                <input ref="linkInput" class="team-links-input" type="text"
                                       v-model="teamLinkDraft" placeholder="https://…"
                                       @click.stop="stopClick" @keyup.enter="saveTeamLink(link.key)"
                                       @keyup.esc="teamLinkEditing = null" />
                                <button type="button" class="team-links-ok" title="Сохранить"
                                        @click.stop="saveTeamLink(link.key)">✓</button>
                                <button type="button" class="team-links-cancel" title="Отмена"
                                        @click.stop="teamLinkEditing = null">×</button>
                            </template>
                        </div>
                    </div>
                </span>

                <div class="name-col">
                    <a :href="teamHref" target="_blank">
                        <h5>{{ teamName }}</h5>
                    </a>
                    <!-- Вторая строка: место в своём чемпионате и разряд турнира
                         («10м (1л)»), следом форма за 3 последних матча — как в онлайн-проге -->
                    <div v-if="standing || form.length" class="team-standing">
                        <span v-if="standing" class="standing-place" :title="standingTitle">{{ standing }}</span>
                        <span v-if="form.length" class="form-strip" :title="formTitle">
                            <span v-for="(f, i) in form" :key="'f' + i"
                                  class="form-item" :class="formClass(f.r)" :title="f.title">{{ f.r }}</span>
                        </span>
                    </div>
                </div>
                <!-- Распорка между названием и окошком вставки. Отдельным элементом, а не
                     отступом: отступ не сжимается, и у длинных названий место простаивало,
                     а имя при этом резалось многоточием -->
                <span class="head-gap"></span>
                <!-- Окошко для вставки картинки с составом. Только админу:
                     распознавание платное -->
                <span
                    v-if="isAdmin"
                    class="ocr-paste"
                    tabindex="0"
                    title="Кликни сюда и вставь картинку состава (Ctrl+V)"
                    @paste="onPasteImage"
                >{{ ocrHint }}</span>
                <!-- У не-админа вместо окошка вставки — такая же по ширине распорка,
                     и она тоже умеет сжиматься: жёсткие 190px резали название -->
                <span class="head-gap head-gap-wide" v-else></span>
            </div>
            <div class="listBox">
                <span class="ico" @click="listBoxShow = !listBoxShow">
                    <img :src="addListImg" alt="add lineup list" />
                </span>
            </div>

            <a class="price-link" :href="`${teamPrice.href}`" target="_blank" v-if="teamPrice !== undefined">
                <h5 v-if="priceOfLineup !== null && priceOfLineup !== 0">
                    €{{ numeral(priceOfLineup).format("0.00a") }}
                </h5>
            </a>
            <a class="price-link" :href="`${team.href}`" target="_blank" v-if="team.href !== undefined">
                <h5>Команда: {{ prettyPrice(team.price) }}</h5>
            </a>
        </div>
        <addListBox :show="listBoxShow" :team="team" @hide="listBoxShow = false" />
        <lineupFromImage
            ref="ocrDialog"
            :show="ocrShow"
            :teamName="teamName"
            :teamsId="id"
            :squad="team.squad || []"
            @close="ocrShow = false"
            @apply="applyOcrLineup"
            @apply-black="applyOcrBlack"
            @missed="missedNames = $event"
        />
        <div class="body">
            <div class="row head_row" :class="getGamerStatus(0)" :style="styleForRow">
                <div class="columns">#</div>
                <div class="columns">Имя</div>
                <div class="columns"></div>
                <div class="columns LU">LU</div>
                <div class="columns" v-if="blueButtons"></div>
                <div class="columns"></div>
                <div class="columns"></div>
                <div class="columns"></div>
                <div class="columns NM" v-if="NM">NM</div>
                <div class="matches">
                    <div v-if="index < matchCount"
                         class="columns m12px w40"
                         v-for="(item, index) in team.matches"
                         :key="index">{{ setMathesName(item) }}</div>
                </div>
                <div class="columns">В</div>
                <div class="columns">П</div>
                <div class="columns minute"></div>

                <div class="columns lineup"></div>
                <div class="columns appearance"></div>

                <div class="columns goal"></div>
                <div class="columns assist"></div>
                <div class="columns yc" v-if="cards"></div>
                <div class="columns y2c" v-if="cards"></div>
                <div class="columns rc" v-if="cards"></div>
                <div class="columns price">€</div>
                <div class="columns remove" v-if="players">R</div>
            </div>
            
            <div class="row"
                 v-show="shouldShowPlayer(rows)"
                 v-for="(rows, index) in team.squad"
                 v-if="team.squad !== undefined"
                 :key="index"
                 :style="styleForRow"
                 :class="{'addBorder': addBorder}">
                <a :href="`//www.transfermarkt.com${rows.link}`" target="_blank" class="links">
                    <div class="columns fs17px">{{ rows.shirtnumber ? rows.shirtnumber : '_' }}</div>
                </a>
                <playerStatus :name="rows.name"
                              :class="getGamerStatus(rows)"
                              @changePlayerStatus="$emit('changePlayerStatus', { data: $event, index })" />
                <gamerStatus v-model="rows.gamerStatus"
                             :id="rows.shirtnumber"
                             :name="rows.name"
                             :teamsId="id"
                             :teamName="teamName" />
                <lineup_status class="columns LU"
                               v-model="rows.LU"
                               :id="rows.shirtnumber"
                               :name="rows.name"
                               :teamsId="id" />
                <lu_button color="blue"
                           v-model="rows.blue"
                           :id="rows.shirtnumber"
                           :names="rows.name"
                           :teamsId="id"
                           :teamName="teamName"
                           v-if="blueButtons" />
                <lu_button color="lightblue"
                           v-model="rows.lightblue"
                           :id="rows.shirtnumber"
                           :names="rows.name"
                           :teamsId="id"
                           :teamName="teamName" />
                <lu_button color="black"
                           v-model="rows.black"
                           :id="rows.shirtnumber"
                           :names="rows.name"
                           :teamsId="id"
                           :teamName="teamName" />
                <div class="columns flag" style="display: flex; justify-content: center; align-items: center; height: 100%; max-width: 200px;">
                    <img v-if="rows.flag" :src="rows.flag.trim()" style="width: 20px; height: auto; display: block;">
                </div>
                <newMatch class="columns"
                          v-model="rows.NM"
                          :id="rows.shirtnumber"
                          :name="rows.name"
                          :teamsId="id"
                          v-if="NM" />
                <div class="matches">
                    <div class="columns m12px"
                         :class="getSquadClass(rows, index)"
                         v-for="(item, index) in team.matches"
                         :key="index"
                         v-if="index < matchCount">
                        <span>&nbsp;</span>
                    </div>
                </div>
                <div class="columns" :style="rows.age >= 14 && rows.age <= 19 ? 'color: #00cc00; font-weight: 700;' : ''">{{ rows.age }}</div>
                <div class="columns">{{ rows.position }}</div>
                <div class="columns">{{ rows["game-minutes"] }}</div>

                <div class="columns">{{ rows.lineups }}</div>
                <div class="columns">{{ rows.appearances }}</div>

                <div class="columns" :class="[getGoals(rows.goals) ? 'max_goals' : '']">{{ rows.goals }}</div>
                <div class="columns" :class="[getAssists(rows.assists) ? 'max_assists' : '']">{{ rows.assists }}</div>
                <div class="columns onHover"
                     :class="{ yellowBorder: rows.yellowCardStatus }"
                     @click="$emit('yellowCard', { data: !rows.yellowCardStatus, index })"
                     v-if="cards">{{ rows["yellow-cards"] }}</div>
                <div class="columns" v-if="cards">{{ rows["2nd-yellow-cards"] }}</div>
                <div class="columns" v-if="cards">{{ rows["red-cards"] }}</div>
                <div class="columns" :class="[isMaxPrice(rows.price) ? 'max_price' : '']">{{ prettyPrice(rows.price) }}</div>

                <div class="columns" v-if="players">
                    <span class="ico"
                          style="position: relative; top: -4px;"
                          @click="$emit('removePlayer', rows)">
                        <img :src="trashImg" alt="Удалить" style="height: 15px;" />
                    </span>
                </div>
            </div>
            
            <div class="row DOP_ROWS"
                 v-show="shouldShowPlayer(rows)"
                 v-for="(rows, index) in team.addsquad"
                 v-if="team.addsquad !== undefined"
                 :key="index + rows.name"
                 :style="styleForRow"
                 :class="{'addBorder': addBorder}">
                <div class="columns fs17px">{{ rows.shirtnumber }}</div>
                <playerStatus :name="rows.name"
                              :class="getGamerStatus(rows)"
                              @changePlayerStatus="
            $emit('changePlayerStatus', {
              data: $event,
              index,
              name: rows.name,
              addsquad: true
            })
          " />
                <gamerStatus v-model="rows.gamerStatus"
                             :id="rows.shirtnumber"
                             :name="rows.name"
                             :teamsId="id"
                             :teamName="teamName" />
                <lineup_status class="columns LU"
                               v-model="rows.LU"
                               :id="rows.shirtnumber"
                               :name="rows.name"
                               :teamsId="id" />
                <lu_button color="blue"
                           v-model="rows.blue"
                           :id="rows.shirtnumber"
                           :names="rows.name"
                           :teamsId="id"
                           :teamName="teamName"
                           v-if="blueButtons" />
                <lu_button color="lightblue"
                           v-model="rows.lightblue"
                           :id="rows.shirtnumber"
                           :names="rows.name"
                           :teamsId="id"
                           :teamName="teamName" />
                <lu_button color="black"
                           v-model="rows.black"
                           :id="rows.shirtnumber"
                           :names="rows.name"
                           :teamsId="id"
                           :teamName="teamName" />
                <div class="columns flag">
                    <span :class="rows.flag"></span>
                </div>
                <newMatch class="columns"
                          v-model="rows.NM"
                          :id="rows.shirtnumber"
                          :name="rows.name"
                          :teamsId="id"
                          v-if="NM" />
                <div class="matches">
                    <div class="columns m12px"
                         :class="getSquadClass(rows, index)"
                         v-for="(item, index) in team.matches"
                         :key="index"
                         v-if="index < matchCount">
                        <span>&nbsp;</span>
                    </div>
                </div>
                <div class="columns" :style="rows.age >= 14 && rows.age <= 19 ? 'color: #00cc00; font-weight: 700;' : ''">{{ rows.age }}</div>
                <div class="columns">{{ rows.position }}</div>
                <div class="columns">{{ rows["game-minutes"] }}</div>

                <div class="columns">{{ rows.lineups }}</div>
                <div class="columns">{{ rows.appearances }}</div>

                <div class="columns" :class="[getGoals(rows.goals) ? 'max_goals' : '']">{{ rows.goals }}</div>
                <div class="columns" :class="[getAssists(rows.assists) ? 'max_assists' : '']">{{ rows.assists }}</div>
                <div class="columns onHover"
                     :class="{ yellowBorder: rows.yellowCardStatus }"
                     @click="$emit('yellowCard', { data: !rows.yellowCardStatus, index })"
                     v-if="cards">{{ rows["yellow-cards"] }}</div>
                <div class="columns" v-if="cards">{{ rows["2nd-yellow-cards"] }}</div>
                <div class="columns" v-if="cards">{{ rows["red-cards"] }}</div>
                <div class="columns" :class="[isMaxPrice(rows.price) ? 'max_price' : '']">{{ prettyPrice(rows.price) }}</div>

                <div class="columns" v-if="players">
                    <span class="ico"
                          style="position: relative; top: -4px;"
                          @click="$emit('removeAddPlayer', rows)">
                        <img :src="trashImg" alt="Удалить" style="height: 15px;" />
                    </span>
                </div>
            </div>
        </div>

        <div class="add_player" v-if="addPlayers" data-html2canvas-ignore="true">
            <div class="form-group flex">
                <input type="text"
                       class="form-control"
                       v-model="addPlayerURL"
                       placeholder="Ссылка на игрока Transfermarkt"
                       @input="getPlayerData($event)"
                       @keyup.enter="getPlayerData($event)" />
                <transition name="fade">
                    <div class="playerLoader" v-if="playerLoader">
                        <preloader />
                    </div>
                </transition>
                <transition name="fade">
                    <select class="form-control" v-model="addPlayerMatch" v-if="playerMatches.length > 0">
                        <option :value="index"
                                v-for="(match, index) in playerMatches"
                                :key="index">
                            {{ match.season }} {{ match.team }}
                        </option>
                    </select>
                </transition>
                <transition name="fade">
                    <button class="btn btn-success"
                            @click="addPlayer()"
                            v-if="playerMatches.length > 0 && addPlayerMatch !== null">
                        Добавить
                    </button>
                </transition>
            </div>
        </div>
        
        <div class="comments" v-if="comments" data-html2canvas-ignore="true">
            <textarea :value="team.comment" @input="handleCommentInput($event.target.value)" />
        </div>
        
        <div class="footer" data-html2canvas-ignore="true">
            <span class="ico trash-btn" title="Отчистить Статус игрока" @click="$emit('clearSavedStatus', id)">
                <img :src="trashImg" alt="delete" />
            </span>

            <span class="ico trash-btn" title="Очистить Цвета" @click="$emit('clearSavedColor', id)">
                <img :src="trashImg" alt="delete" />
            </span>
            
            <span class="ico trash-btn" title="Отчистить LU" @click="$emit('clearSavedLU', id)">
                <img :src="trashImg" alt="delete" />
            </span>

            <switches caption="BB" v-model="blueButtons" />
            <switches caption="NM" v-model="NM" />

            <switches caption="Уда.И" v-model="players" title="Удалить игрока" />
            <switches caption="Доб.И" v-model="addPlayers" title="Добавить игрока" />
            
            <switches caption="Комм" v-model="comments" title="Комментарий" />

            <span class="ico master-trash-btn" 
                  title="Полная очистка Цветов и LU (Команда А и Б)" 
                  v-if="masterBtn" 
                  @click="$emit('masterClear')">
                <img :src="trashImg" alt="master delete" />
            </span>

        </div>
    </div>
</template>

<script>
    import lu_button from "./lu_button.vue";
    import gamerStatus from "./gamerStatus";
    import lineup_status from "./lineup_status";
    import newMatch from "./newMatch";
    import trashImg from "../assets/img/trash.svg";
    import addListImg from "../assets/img/add_list.svg";
    import numeral from "numeral";
    import addListBox from "@/components/addListBox";
    import switches from "@/components/switches.vue";
    import preloader from "@/components/preloader";
    import playerStatus from "@/components/playerStatus";
    import priceSelecter from "@/components/priceSelecter";
    import lineupFromImage from "@/components/lineupFromImage";

    // Логотипы соцсетей рисуем инлайновым SVG: картинок в проекте нет, а тянуть их
    // со стороны нельзя — окно должно открываться мгновенно и без внешних запросов
    const SOCIAL_ICONS = {
        instagram: '<svg viewBox="0 0 24 24" fill="#E4405F" width="15" height="15"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.08 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.67 1.08-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zM12 16a4 4 0 110-8 4 4 0 010 8zm7.85-10.41a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z"/></svg>',
        twitter: '<svg viewBox="0 0 24 24" fill="#000000" width="15" height="15"><path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.25 6.93zm-1.29 19.49h2.04L6.49 3.24H4.3z"/></svg>',
        facebook: '<svg viewBox="0 0 24 24" fill="#1877F2" width="15" height="15"><path d="M24 12.07C24 5.44 18.63.07 12 .07S0 5.44 0 12.07c0 5.99 4.39 10.95 10.13 11.85v-8.38H7.08v-3.47h3.05V9.43c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95h-1.51c-1.49 0-1.96.93-1.96 1.87v2.25h3.33l-.53 3.47h-2.8v8.38C19.61 23.02 24 18.06 24 12.07z"/></svg>'
    };

    export default {
        name: "squadTable",
        components: {
            lu_button,
            gamerStatus,
            lineup_status,
            newMatch,
            addListBox,
            switches,
            preloader,
            playerStatus,
            priceSelecter,
            lineupFromImage,
        },
        data: () => ({
            numeral,
            trashImg,
            addListImg,
            listBoxShow: false,
            ocrShow: false,
            ocrHint: "вставь",
            missedNames: [],
            formTitle: 'Форма за 3 последних матча: самый свежий — слева',
            teamLinksShow: false,
            teamLinksLoading: false,
            teamLinks: null,
            teamLinksPending: false,   // фоновый поиск соцсетей ещё идёт
            teamLinksTimer: null,
            teamLinkEditing: null,     // ключ строки, которую правят руками
            teamLinkDraft: '',
            comments: false,
            NM: false,
            players: false,
            addPlayers: false,
            addPlayerURL: "",
            addPlayerMatch: null,
            playerMatches: [],
            playerData: {},
            playerLoader: false,
            blueButtons: false,
            commentTimer: null, 
        }),
        props: {
            id: "",
            teamName: "",
            team: null,
            teamPrice: null,
            teamHref: "",
            lineupPrice: null,
            priceOfLineup: { type: Number, default: null },
            matchCount: { type: String },
            cards: { type: Boolean, default: false },
            addBorder: { type: Boolean, default: false },
            masterBtn: { type: Boolean, default: false },
            // "A"/"B" — чтобы расширение знало, в какую половину слать картинку
            side: { type: String, default: "" }
        },
        mounted() {
            window.addEventListener("message", this.onExtensionImage);
            document.addEventListener("click", this.closeTeamLinks);
        },
        beforeDestroy() {
            window.removeEventListener("message", this.onExtensionImage);
            document.removeEventListener("click", this.closeTeamLinks);
        },
        methods: {
            // 🔥 НОВЫЙ МЕТОД: Проверка всех 3 факторов (Цена, Номер, 3 Матча)
            shouldShowPlayer(player) {
                // Если глазик открыт (фильтр выключен) - показываем всех игроков без разбора
                if (!this.isGhostsHidden) return true;

                // Ушедшие из команды прячутся сразу, отдельно от трёх факторов ниже.
                // Иначе они почти никогда не скрываются: за выбранный сезон они играли,
                // и фактор "не играл в 3 матчах" не срабатывает.
                const status = Number(player.gamerStatus) || 0;

                // Сплошной красный самолёт — прячем
                if (status === 6) return false;

                // Перечёркнутый самолёт (10) и любой другой выставленный руками статус
                // оставляют игрока на виду: ручной выбор важнее авто-пометки
                if (status === 0 && player.leftTeam) return false;

                // 1. ФАКТОР: Нет номера (пусто, null, '0' или '-')
                const noNumber = !player.shirtnumber || String(player.shirtnumber).trim() === '0' || String(player.shirtnumber).trim() === '-';

                // 2. ФАКТОР: Нет цены (пусто, null, '0' или '-')
                const noPrice = !player.price || String(player.price).trim() === '-' || String(player.price).trim() === '0' || String(player.price).trim() === '';

                // 3. ФАКТОР: Пусто в первых 3-х матчах
                let playedInLast3 = false;
                
                // Проверяем 3 последних матча. Если их спарсено меньше 3, проверяем сколько есть.
                const matchesToCheck = Math.min(3, this.team.matches ? this.team.matches.length : 0);
                
                for (let i = 0; i < matchesToCheck; i++) {
                    // Используем уже готовые методы, которые определяют цвет кружочка (играл или сидел на замене)
                    const colorSquad = this.getSquadClass(player, i);
                    const colorAddSquad = this.getAddedSquadClass(player, i);
                    
                    if (colorSquad === 'roundgreen' || colorSquad === 'roundyellow' || 
                        colorAddSquad === 'roundgreen' || colorAddSquad === 'roundyellow') {
                        playedInLast3 = true;
                        break;
                    }
                }
                const noGames = !playedInLast3;

                // Если совпали ВСЕ 3 фактора, значит это "призрак", прячем его (возвращаем false)
                if (noNumber && noPrice && noGames) {
                    return false; 
                }

                // Иначе показываем (возвращаем true)
                return true;
            },

            handleCommentInput(val) {
                clearTimeout(this.commentTimer);
                this.commentTimer = setTimeout(() => {
                    this.$emit('setComment', val);
                }, 1000);
            },
            setMatchSquad(rows, index, val) {
                const match = { ...this.team.matches[index] };
                match.addsquad === undefined ? (match.addsquad = []) : false;
                const getNewVal = (val) =>
                    val === "squad"
                        ? "substitution"
                        : val === "substitution"
                            ? "none"
                            : val === "none" && "squad";
                const find = match.addsquad.find(
                    (item) => item.shirtnumber === rows.shirtnumber
                );
                !find
                    ? match.addsquad.push({
                        flag: rows.flag,
                        name: rows.name,
                        shirtnumber: rows.shirtnumber,
                        squad: val,
                    })
                    : match.addsquad.map((item) =>
                        item.shirtnumber === rows.shirtnumber
                            ? (item.squad = getNewVal(item.squad))
                            : 0
                    );
                this.$emit("setMatchSquad", { match, index });
            },
            async getPlayerData() {
                this.playerLoader = true;
                this.playerMatches = [];
                this.playerData = {};
                this.addPlayerMatch = null;
                
                const reg = /transfermarkt\..*\/profil\/spieler\//;
                
                if (reg.test(this.addPlayerURL)) {
                    const { data } = await this.$store.dispatch("GET_PLAYER_DATA", {
                        url: this.addPlayerURL,
                        targetTeam: this.teamName
                    });
                    this.playerData = data;
                    this.playerMatches = data.matches;
                    this.playerLoader = false;
                } else {
                    this.playerLoader = false;
                }
            },
            
            addPlayer() {
                const player = {
                ...this.playerData.player,
                ...this.playerData.matches[this.addPlayerMatch],
                };
                this.addPlayerURL = "";
                this.playerMatches = [];
                this.$emit("addPlayer", player);
            },
            /** Дата прошлого матча ТМ («dd/mm/yy») → миллисекунды, для сортировки формы. */
            prevMatchTime(m) {
                const date = m && m.teamData && m.teamData.date;
                if (!date || !date.includes('/')) return 0;
                const p = date.split('/');
                return new Date(`20${p[2]}-${p[1]}-${p[0]}`).getTime() || 0;
            },
            /** Имя клуба для сравнения: без регистра, пунктуации и приставок. */
            normTeam(name) {
                return String(name || '')
                    .toLowerCase()
                    .replace(/[^a-zа-яё0-9]+/gi, '')
                    .trim();
            },
            formClass(r) {
                return r === 'W' ? 'form-w' : r === 'L' ? 'form-l' : 'form-d';
            },
            setMathesName(match) {
                const comp = match.teamData.competition.name;
                const date = match.teamData.date
                    .match(/(\d+\/\d+)\/\d+/)[1]
                    .replace("/", ".");
                return `${comp}\n${date}`;
            },
            getGamerStatus(row) {
                return ` ${row.playerStatus} `;
            },

            // В буфере может лежать что угодно — берём первую картинку
            onPasteImage(event) {
                const items = (event.clipboardData || window.clipboardData || {}).items || [];
                const file = Array.from(items)
                    .filter(i => i.type && i.type.indexOf('image') === 0)
                    .map(i => i.getAsFile())
                    .find(Boolean);

                if (!file) {
                    this.ocrHint = "не картинка";
                    setTimeout(() => { this.ocrHint = "вставь"; }, 2000);
                    return;
                }
                event.preventDefault();

                const reader = new FileReader();
                reader.onload = () => {
                    this.ocrShow = true;
                    // Ждём отрисовки окна, иначе его метода ещё нет
                    this.$nextTick(() => this.$refs.ocrDialog.recognize(reader.result));
                };
                reader.readAsDataURL(file);
            },

            // Картинка прилетела из расширения по Alt+клику на чужом сайте
            // Ссылки клуба тянем один раз на команду и держим до перезагрузки страницы
            async toggleTeamLinks() {
                this.teamLinksShow = !this.teamLinksShow;
                if (!this.teamLinksShow || this.teamLinks) return;

                this.teamLinksLoading = true;
                await this.fetchTeamLinks({ fast: true });
                this.teamLinksLoading = false;
            },

            /**
             * Запрос ссылок. fast — бэкенд отдаёт найденное сразу, а поиск соцсетей
             * уводит в фон и помечает ответ «pending»; тогда переспрашиваем раз в 2 с.
             */
            async fetchTeamLinks({ fast = false, refresh = false } = {}) {
                try {
                    const { data } = await this.$axios.get('/team-links', {
                        params: { name: this.teamName, fast, refresh },
                        headers: { Authorization: this.$cookie ? this.$cookie.get('token') : '' }
                    });
                    const links = data || {};
                    this.teamLinksPending = links.pending === '1';
                    this.teamLinks = links;
                    this.scheduleTeamLinksPoll();
                } catch (e) {
                    console.warn('Ссылки клуба не загрузились:', e);
                    this.teamLinks = this.teamLinks || {};
                    this.teamLinksPending = false;
                }
            },

            scheduleTeamLinksPoll() {
                clearTimeout(this.teamLinksTimer);
                if (!this.teamLinksPending || !this.teamLinksShow) return;
                this.teamLinksTimer = setTimeout(() => this.fetchTeamLinks({ fast: true }), 2000);
            },

            startEditLink(link) {
                this.teamLinkEditing = link.key;
                // В поле подставляем уже найденную ссылку, а не ссылку на поиск
                this.teamLinkDraft = link.search ? '' : link.url;
                this.$nextTick(() => {
                    const el = this.$refs.linkInput;
                    const input = Array.isArray(el) ? el[0] : el;
                    if (input) input.focus();
                });
            },

            async saveTeamLink(key) {
                const url = (this.teamLinkDraft || '').trim();
                this.teamLinkEditing = null;
                try {
                    const { data } = await this.$axios.post('/team-links/manual',
                        { name: this.teamName, key, url },
                        { headers: { Authorization: this.$cookie ? this.$cookie.get('token') : '' } });
                    this.teamLinks = data || {};
                    this.teamLinksPending = (data || {}).pending === '1';
                    this.scheduleTeamLinksPoll();
                } catch (e) {
                    console.warn('Ссылку сохранить не удалось:', e);
                }
            },

            // Пустышка для @click.stop: сам модификатор без обработчика во Vue 2 не работает
            stopClick() {},

            async reloadTeamLinks() {
                this.teamLinks = null;
                this.teamLinksLoading = true;
                await this.fetchTeamLinks({ refresh: true });
                this.teamLinksLoading = false;
            },

            closeTeamLinks() {
                this.teamLinksShow = false;
                this.teamLinkEditing = null;
                clearTimeout(this.teamLinksTimer);
            },

            onExtensionImage(event) {
                if (event.source !== window) return;
                const d = event.data;
                if (!d || d.source !== "offpars-ext" || d.type !== "OFFPARS_IMAGE") return;
                if (d.side !== this.side) return;

                this.ocrShow = true;
                this.$nextTick(() => this.$refs.ocrDialog.recognize(d.image));
            },

            applyOcrLineup(rows) {
                this.$store.dispatch("APPLY_LINEUP_FROM_IMAGE", { teamsId: this.id, rows })
                    .then(res => {
                        if (res && res.applied === 0) alert("Ничего не проставилось.");
                    });
            },

            // Заявка с картинки: чёрная точка вместо LU
            applyOcrBlack(rows) {
                if (!rows || rows.length === 0) return;
                const payload = rows.map(r => ({
                    teamsId: this.id,
                    id: r.shirtnumber,
                    name: r.name,
                    color: 'black'
                }));
                this.$store.dispatch("SAVE_COLOR_BATCH", payload);
            },

            getSquadClass(player, matchNumber) {
                let color;
                if (this.team.matches[matchNumber].squad !== undefined) {
                    const playerLink = player.link;
                    this.team.matches[matchNumber].squad.forEach((item) => {
                        const itemLink = item.link;
                        if (playerLink === itemLink) {
                            if (item.squad === "squad") {
                                color = "roundgreen";
                            } else if (item.squad === "substitution") {
                                color = "roundyellow";
                            }
                        }
                    });
                    return color;
                } else {
                    return "";
                }
            },
            getAddedSquadClass(player, matchNumber) {
                let color;
                if (this.team.matches[matchNumber].addsquad !== undefined) {
                    const playerName = this.getName(player.name);
                    this.team.matches[matchNumber].addsquad.forEach((item) => {
                        if (item.shirtnumber === 0) {
                            const itemName = this.getName(item.name);
                            if (playerName === itemName) {
                                if (item.squad === "squad") {
                                    color = "roundgreen";
                                } else if (item.squad === "substitution") {
                                    color = "roundyellow";
                                }
                            }
                        } else {
                            if (item.shirtnumber === player.shirtnumber) {
                                if (item.squad === "squad") {
                                    color = "roundgreen";
                                } else if (item.squad === "substitution") {
                                    color = "roundyellow";
                                }
                            }
                        }
                    });
                    return color;
                } else {
                    return "";
                }
            },
            getAssists(numAssists) {
                return this.assist_max === Number(numAssists) && Number(numAssists) > 0 ? true : false;
            },
            getGoals(numGoals) {
                return this.goal_max === Number(numGoals) && Number(numGoals) > 0 ? true : false;
                
            },
            isMaxPrice(price) {
                if (!price) return false;
                var num = parseFloat(price.replace(/[^\d.]/g, ''));
                const suffix = price.toLowerCase().slice(-1);

                if (suffix === 'm') {
                    num = num * 1000000;
                } else if (suffix === 'k') {
                    num = num * 1000;
                }
                return this.price_max === num ? true : false;
            },
            getMaxPrice(player) {
                const price = this.getNumber(
                    this.getPlayerPrice({ player, squadList: this.lineupPrice, playerNameField: 'name', playerShirtnumberField: 'shirtnumber', squadNameField: 'name', squadShirtnumberField: 'number', squadListFieldReturn: 'price' })
                );
                return this.price_max > 0 && price === this.price_max ? true : false;
            },
        },
        watch: {
            // При переключении матча Vue переиспользует эту же таблицу, меняя лишь
            // свойства. Всё, что мы нашли для прошлой команды, живёт в локальном
            // состоянии и без сброса переезжает в новый матч: ссылки на соцсети
            // показывались от предыдущего клуба, плашка ненайденных — от прошлого разбора
            id() {
                this.teamLinks = null;
                this.teamLinksShow = false;
                this.teamLinksLoading = false;
                this.teamLinksPending = false;
                this.teamLinkEditing = null;
                clearTimeout(this.teamLinksTimer);
                this.missedNames = [];
            }
        },

        computed: {
            teamLinksList() {
                const l = this.teamLinks || {};

                // У официального сайта своего логотипа нет — идёт без значка
                const rows = [{ key: 'site', label: 'Официальный сайт', icon: null, domain: null },
                    { key: 'instagram', label: 'Instagram', icon: SOCIAL_ICONS.instagram, domain: 'instagram.com' },
                    { key: 'twitter', label: 'X (Twitter)', icon: SOCIAL_ICONS.twitter, domain: 'x.com' },
                    { key: 'facebook', label: 'Facebook', icon: SOCIAL_ICONS.facebook, domain: 'facebook.com' }];

                const manual = String(l.manual || '').split(',').filter(Boolean);

                return rows.reduce((acc, i) => {
                    if (l[i.key]) {
                        acc.push({ key: i.key, label: i.label, url: l[i.key], icon: i.icon,
                                   search: false, manual: manual.indexOf(i.key) !== -1 });
                        return acc;
                    }

                    // Сайт не нашёлся — строка всё равно нужна: её можно заполнить карандашом
                    if (!i.domain) {
                        const qs = encodeURIComponent(`${this.teamName} official site`);
                        acc.push({
                            key: i.key,
                            label: `${i.label} — поиск`,
                            url: `https://www.google.com/search?q=${qs}`,
                            icon: null,
                            search: true,
                            manual: false
                        });
                        return acc;
                    }

                    // Часть клубов не заведена в Wikidata, а их сайт закрыт защитой
                    // (Calcio Padova — Cloudflare 403). Тогда даём ссылку на поиск:
                    // в браузере пользователя он открывается без всяких блокировок
                    if (i.domain) {
                        const q = encodeURIComponent(`"${this.teamName}" site:${i.domain}`);
                        acc.push({
                            key: i.key,
                            label: `${i.label} — поиск`,
                            url: `https://www.google.com/search?q=${q}`,
                            icon: i.icon,
                            search: true,
                            manual: false
                        });
                    }
                    return acc;
                }, []);
            },

            // Окошко вставки картинки видно только админу. Это удобство, а не защита:
            // сам эндпоинт тоже проверяет права, иначе им мог бы пользоваться кто угодно
            isAdmin() {
                return this.$store.getters.premissions === "admin";
            },

            // 🔥 НОВОЕ СВОЙСТВО: Привязываемся к переменной во Vuex
            isGhostsHidden() {
                return this.$store.state.hideGhostPlayers || false;
            },

            styleForRow() {
                const getPlayer = (player) => (player ? "40px" : "");
                const getNMBlueButtons = (NM, btn) => {
                    if (NM && btn) {
                        return "repeat(7, 25px)";
                    } else if (NM && !btn) {
                        return "repeat(6, 25px)";
                    } else if (!NM && btn) {
                        return "repeat(6, 25px)";
                    } else {
                        return "repeat(5, 25px)";
                    }
                };
                const getCards = (card) => (card ? "repeat(7, 25px)" : "repeat(4, 25px)");
                const getMatches = () => this.matchCount * 40 + 3;
                
                return `grid-template-columns: 25px 180px ${getNMBlueButtons(
                    this.NM,
                    this.blueButtons
                )} ${getMatches()}px repeat(2, 25px) 40px ${getCards(
                    this.cards
                )} 65px ${getPlayer(this.players)};`;
            },
            /** «10м (1л)» — место в таблице и разряд турнира. Пусто, если ТМ не дал ничего. */
            standing() {
                const place = this.team && this.team.tablePosition;
                const tier = this.team && this.team.leagueLevel;
                if (!place && !tier) return '';
                const placeText = place ? `${place}м` : '–';
                return tier ? `${placeText} (${tier}л)` : placeText;
            },
            standingTitle() {
                const parts = [];
                if (this.team && this.team.leagueName) parts.push(this.team.leagueName);
                if (this.team && this.team.tablePosition) parts.push(`место в таблице: ${this.team.tablePosition}`);
                if (this.team && this.team.leagueLevel) parts.push(`разряд турнира: ${this.team.leagueLevel}`);
                return parts.join(' · ') || 'Место в чемпионате';
            },
            /**
             * Форма за 3 последних сыгранных, самый свежий слева.
             * Результат считаем сами: в прошлых матчах лежит только счёт и пара команд,
             * поэтому сторону определяем по названию своей команды.
             */
            form() {
                const arr = (this.team && this.team.matches) || [];
                const mine = this.normTeam(this.teamName || (this.team && this.team.name));
                const out = [];
                for (const m of [...arr].sort((a, b) => this.prevMatchTime(b) - this.prevMatchTime(a))) {
                    const td = m && m.teamData;
                    if (!td) continue;
                    const score = String(td.score || '').match(/(\d+)\s*:\s*(\d+)/);
                    if (!score) continue;                       // «-:-» и отменённые пропускаем
                    const home = this.normTeam(td.teamA);
                    const away = this.normTeam(td.teamB);
                    const isHome = mine && home && (home === mine || home.includes(mine) || mine.includes(home));
                    const isAway = mine && away && (away === mine || away.includes(mine) || mine.includes(away));
                    if (!isHome && !isAway) continue;           // своей команды в паре нет
                    const my = Number(isHome ? score[1] : score[2]);
                    const other = Number(isHome ? score[2] : score[1]);
                    const r = my > other ? 'W' : my < other ? 'L' : 'D';
                    const comp = (td.competition && (td.competition.title || td.competition.name)) || '';
                    const line = `${td.teamA} - ${td.teamB} ${score[1]}-${score[2]}` + (td.date ? ` (${td.date})` : '');
                    out.push({ r, title: comp ? `${line}
${comp}` : line });
                    if (out.length === 3) break;
                }
                return out;
            },
            price_max() {
                let prices = [];
                if (!this.isEmptyObject(this.team.squad)) {
                    this.team.squad.forEach(async (item) => {
                        var num = parseFloat(item.price.replace(/[^\d.]/g, ''));
                        const suffix = item.price.toLowerCase().slice(-1);

                        if (suffix === 'm') {
                            num = num * 1000000;
                        } else if (suffix === 'k') {
                            num = num * 1000;
                        }
                        prices.push(num);
                    });
                }
                return this.MaxOnArray(prices);
            },
            goal_max() {
                let goals = [];
                if (!this.isEmptyObject(this.team.squad)) {
                    this.team.squad.forEach(async (item) => {
                        goals.push(item.goals);
                    });
                }
                return this.MaxOnArray(goals);
            },
            assist_max() {
                let assists = [];
                if (!this.isEmptyObject(this.team.squad)) {
                    this.team.squad.forEach((item) => {
                        assists.push(item.assists);
                    });
                }
                return this.MaxOnArray(assists);
            }
        },
        watched() {
            const mt = this.team.matches;
        },
    };
</script>

<style lang="sass">
// Эмблема: на наведении подрастает, по клику под ней открывается окошко со ссылками
.team-logo
  position: relative
  display: inline-flex
  align-items: center
  cursor: pointer
  img
    height: 25px
    width: 25px
    margin: 0 10px 0 0
    transition: transform .15s ease
    transform-origin: center
  &:hover img
    transform: scale(2)

.team-links
  position: absolute
  top: 30px
  left: 0
  z-index: 500
  display: flex
  flex-direction: column
  min-width: 190px
  padding: 6px 8px
  border: 1px solid #c9c9c9
  border-radius: 5px
  background: #fff
  box-shadow: 0 4px 14px rgba(0, 0, 0, .18)
  font-size: 15px
  cursor: default
  a
    display: flex
    align-items: center
    gap: 7px
    padding: 4px 0
    color: #1565c0
    text-decoration: none
    white-space: nowrap
    &:hover
      text-decoration: underline

.team-links-search
  color: #8a8a8a !important
  .team-links-ico
    opacity: .55

.team-links-ico
  display: inline-flex
  align-items: center
  flex: none
  width: 15px
  height: 15px

// Строка окошка: ссылка слева, карандаш справа
.team-links-row
  display: flex
  align-items: center
  gap: 4px
  a
    flex: 1 1 auto
    min-width: 0
  &:hover .team-links-edit
    opacity: 1

.team-links-manual
  color: #2e7d32 !important

.team-links-edit
  flex: none
  border: none
  background: none
  cursor: pointer
  padding: 0 2px
  font-size: 14px
  line-height: 1
  color: #9aa0a6
  opacity: .35
  transition: opacity .15s
  &:hover
    color: #2196f3

.team-links-input
  flex: 1 1 auto
  min-width: 0
  border: 1px solid #c7c7c7
  border-radius: 3px
  padding: 2px 4px
  font-size: 12px

.team-links-ok,
.team-links-cancel
  flex: none
  border: none
  background: none
  cursor: pointer
  font-size: 15px
  line-height: 1
  padding: 0 2px

.team-links-ok
  color: #2e7d32

.team-links-cancel
  color: #c62828

.team-links-head
  display: flex
  align-items: center
  justify-content: space-between
  gap: 8px
  margin-bottom: 4px
  padding-bottom: 4px
  border-bottom: 1px solid #eee
  font-size: 15px
  font-weight: 600
  white-space: nowrap

.team-links-name
  flex: 1
  overflow: hidden
  text-overflow: ellipsis

.team-links-refresh
  flex: none
  display: inline-flex
  align-items: center
  border: none
  background: none
  padding: 0
  cursor: pointer
  &:disabled
    opacity: .5
    cursor: default

.team-links-close
  flex: none
  border: none
  background: none
  padding: 0 2px
  color: #444
  font-size: 24px
  line-height: 1
  cursor: pointer
  &:hover
    color: #000

.team-links-empty
  padding: 2px 0
  color: #777

// Плашка с игроками старта, которых нет в ростере: висит над шапкой команды,
// красная — чтобы её нельзя было не заметить, и снимается кликом
.ocr-missed
  display: flex
  align-items: flex-start
  gap: 8px
  margin: 0 0 4px
  padding: 4px 8px
  border: 2px solid #c62828
  border-radius: 4px
  color: #c62828
  font-size: 13px
  font-weight: 600
  line-height: 1.3

.ocr-missed-text
  flex: 1
  -webkit-user-select: text
  -moz-user-select: text
  -ms-user-select: text
  user-select: text
  cursor: text

.ocr-missed-close
  flex: none
  border: none
  background: none
  padding: 0 2px
  color: #c62828
  font-size: 16px
  line-height: 1
  cursor: pointer

@import "../assets/vars"

.master-trash-btn
  display: inline-flex
  align-items: center
  justify-content: center
  width: 40px !important
  height: 40px !important
  background-color: #dc3545 !important
  border-radius: 6px
  cursor: pointer
  margin-left: 20px
  transition: all 0.2s ease
  box-shadow: 0 4px 6px rgba(220, 53, 69, 0.3)
  
  img
    width: 20px !important
    height: 20px !important
    filter: brightness(0) invert(1)
    
  &:hover
    background-color: #c82333 !important
    transform: scale(1.1)

.squadTable .body .row .columns
  &.roundblue, &.roundred, &.roundyellow, &.roundgreen, &.roundlightblue, &.roundblack,
  &.LU, 
  &[class*="round"] 
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none
    user-select: none
    -webkit-tap-highlight-color: transparent
    -webkit-touch-callout: none

    &:focus
      outline: none

    span
      -webkit-user-select: none
      -moz-user-select: none
      -ms-user-select: none
      user-select: none
      pointer-events: none

.squadTable .body .row .columns
  &.name,
  &.flag,
  &.minute,
  &.appearance,
  &.lineup,
  &.goal,
  &.assist,
  &.yc,
  &.y2c,
  &.rc,
  &.price,
  &.remove,
  &.m12px,
  &.w40,
  &.fs17px
    -webkit-user-select: text
    -moz-user-select: text
    -ms-user-select: text
    user-select: text

.footer
  display: flex
  align-items: center

.none
  color: black
  background-color: transparent

.ocr-paste
  width: 90px
  height: 30px
  border: 2px solid $RoundRed
  border-radius: 3px
  background: #fff
  cursor: text
  font-size: 11px
  color: #999
  display: flex
  align-items: center
  justify-content: center
  &:focus
    outline: none
    border-color: #639922
    color: #639922

.red_text
  color: $RoundRed
  text-decoration: line-through

.red_bg_white_text
  background-color: rgba($RoundRed,.5)
  color: white

.green_text
  color: $RoundGreen

.yellow_text
  color: $RoundBroun

.black_bg_white_text
  background-color: black
  color: white

.yellowBorder
  border: 3px solid $RoundYellow !important
  box-sizing: border-box

.w40
  width: 40px !important

.max_goals
  border: 3px solid $RoundRed !important
  box-sizing: border-box

.max_assists
   border: 3px solid $RoundRed !important
   box-sizing: border-box

.max_price
  border: 3px solid $RoundGreen !important
  box-sizing: border-box

.comments
  width: 100%
  textarea
    width: 100%
    height: auto
    min-height: 100px

.teamname
  display: flex
  align-items: center
  span
    margin: 0 5px
  // Название и строка «10м (1л)» идут колонкой: место с формой встаёт ПОД названием
  .name-col
    display: flex
    flex-direction: column
    align-items: flex-start
    a
      display: flex
      align-items: center
  // Промежуток до окошка вставки: до 105px, но при нехватке места схлопывается
  // ПЕРВЫМ — коэффициент сжатия намеренно огромный, иначе браузер ужимал заодно
  // и название, и короткое «FC Rapperswil-Jona» резалось многоточием
  // Промежуток до окошка вставки: минимум 105px, растягивается на остаток свободного
  // места и схлопывается ПЕРВЫМ, когда названию тесно
  .head-gap
    flex: 1 1 105px
    min-width: 0
    margin: 0
  .head-gap-wide
    flex-basis: 90px
  .ocr-paste
    margin-left: 0
    flex: 0 0 auto
  // Строка «9м (2л) W L D» — свои отступы: иначе `.teamname span` разносит
  // квадраты формы на 5px друг от друга
  .team-standing span
    margin: 0

.team-standing
  display: flex
  align-items: center
  flex-wrap: nowrap
  white-space: nowrap
  gap: 8px
  margin-top: 2px

.standing-place
  font-weight: 700
  font-size: .97rem
  color: #2c3e50
  cursor: help

.form-strip
  display: inline-flex
  gap: 3px

.form-item
  width: 19.4px
  height: 19.4px
  border-radius: 3px
  display: inline-flex
  align-items: center
  justify-content: center
  font-size: .83rem
  font-weight: 700
  color: #fff

.form-w
  background: #28a745

.form-l
  background: #dc3545

.form-d
  background: #94a3b8

.listBox
  display: flex
  align-items: center

.squadTable
  position: relative
  display: grid
  justify-content: center
  align-content: start
  margin: 0 25px
  .captions
    // Эмблема с названием прижата влево — встаёт над колонкой «#», а ценники
    // уходят к правому краю
    display: flex
    justify-content: flex-start
    min-height: 40px
    align-items: center
    // Ширину задаёт ТАБЛИЦА, а не шапка: иначе длинное название клуба раздвигало
    // колонку грида, таблица оставалась по центру, и эмблема вылезала за её левый
    // край. Переносов нет — не влезающее название обрезается многоточием
    width: 0
    min-width: 100%
    // ⚠️ overflow: hidden тут ставить НЕЛЬЗЯ — он обрезает окошко со ссылками клуба,
    // которое всплывает из эмблемы вниз. За края таблицы шапка и так не вылезает:
    // её ширину задаёт width: 0 + min-width: 100%
    // Блок названия забирает ВСЁ свободное место: иначе его съедал авто-отступ
    // иконки заявки, а названию доставалась только его исходная ширина — и оно
    // резалось многоточием при свободной полосе рядом
    .teamname
      min-width: 0
      flex: 1 1 auto
      // Название НЕ сжимается: место отдают распорки. Иначе при тесноте браузер
      // ужимал его вместе с ними — имя резалось многоточием, хотя рядом пустота
      .name-col
        min-width: 0
        flex: 0 0 auto
        a
          max-width: 100%
        h5
          overflow: hidden
          text-overflow: ellipsis
          white-space: nowrap
    // Ценники не переносятся и не сжимаются: место при нехватке отдаёт название
    a.price-link
      white-space: nowrap
      flex: 0 0 auto
    // Иконка заявки уезжает вправо и встаёт вплотную к цене стартового состава
    .listBox
      flex: 0 0 auto
    a.price-link:first-of-type
      margin-left: 10px
    a.price-link + a.price-link
      margin-left: 18px
    a
      display: flex
      flex-wrap: nowrap
      align-items: center
      h5
        font-size: 1.2em
        padding: 0
        margin: 0
      img
        height: 25px
        width: 25px
        margin: 0 10px 0 0
        position: relative
    h5
      font-size: 1.2em
      padding: 0
      margin: 0
  .body
    display: grid
    justify-content: center
    .row
      display: inline-grid
      width: auto
      height: auto
      &.addBorder
        border: 1px solid black
      .matches
        display: grid
        grid-template-columns: repeat(auto-fit, minmax(25px , 1fr))
      &:hover
        background-color: rgba($RoundLightBlue,.4) !important
      div
        border: .8px solid rgba(white,.7)
      &.NM
        color: $RoundYellow
        background-color: rgba(black,.5) !important
      &:nth-child(2n)
        background-color: rgba(#dadada,.4)
        &.red_bg_white_text
          background-color: rgba($RoundRed,.5)
          color: white
      .columns
        min-width: 25px
        width: auto
        min-height: 25px
        font-weight: 500
        height: auto
        text-align: center
        display: grid
        align-content: center
        justify-content: center
        &.name
          text-align: left
        &.flag span
          height: 15px
          transform: scale(1.2)
        &.roundblue, &.roundred, &.roundyellow, &.roundgreen, &.roundlightblue, &.roundblack
          cursor: pointer
          span
            border-radius: 50%
            width: 17px
            height: 17px
            display: block
            margin: 0 auto
          &.active:hover::after
            display: none
          &.LU:hover::after
            display: none
        &.roundred
          span
            border: 1px solid $RoundRed
            background-color: $RoundRed
        &.roundyellow
          span
            border: 1px solid $RoundYellow
            background-color: $RoundYellow
        &.roundgreen
          span
            border: 1px solid $RoundGreen
            background-color: $RoundGreen
        &.roundblue
          span
            border: 1px solid $RoundBlue
            background-color: $RoundBlue
          &:hover
            &::after
              content: ''
              border-radius: 50%
              width: 17px
              height: 17px
              display: block
              margin: 0 auto
              border: 1px solid $RoundBlue
              background-color: $RoundBlue
              opacity: .5
        &.roundlightblue
          span
            border: 1px solid $RoundLightBlue
            background-color: $RoundLightBlue
          &:hover
            &::after
              content: ''
              border-radius: 50%
              width: 17px
              height: 17px
              display: block
              margin: 0 auto
              border: 1px solid $RoundLightBlue
              background-color: $RoundLightBlue
              opacity: .5
        &.roundblack
          span
            border: 1px solid black
            background-color: black
          &:hover
            &::after
              content: ''
              border-radius: 50%
              width: 17px
              height: 17px
              display: block
              margin: 0 auto
              border: 1px solid black
              background-color: black
              opacity: .5
    .head_row
      height: auto !important
      .columns
        min-width: 25px
        width: auto
        min-height: 25px
        height: auto
        text-align: center
        display: grid
        align-content: center
        justify-content: center
        &.minute, &.appearance, &.lineup, &.goal, &.assist, &.yc, &.y2c, &.rc
          position: relative
          &::after
            position: relative
            content: ""
            background-image: url('../assets/img/table/minute_played.png')
            background-position: center
            background-size: 15px 15px
            width: 15px
            height: 15px
        &.appearance::after
          background-image: url('../assets/img/table/appearance.png')
        &.lineup::after
          background-image: url('../assets/img/table/L.png')
        &.goal::after
          background-image: url('../assets/img/table/G.png')
        &.assist::after
          background-image: url('../assets/img/table/AS.png')
        &.yc::after
          background-image: url('../assets/img/table/YC.png')
        &.y2c::after
          background-image: url('../assets/img/table/Y2C.png')
        &.rc::after
          background-image: url('../assets/img/table/RC.png')
        &.LU.err
          background-color: rgba($RoundRed, .5)
        &.m10px
          font-size: 10px
          font-weight: 700
        &.m12px
          font-size: 10px

.form-group.flex
  display: grid
  grid-gap: 10px
  grid-template-columns: repeat(auto-fit, minmax(290px,1fr) )
  margin: 10px 0 !important
  transition: all .3s ease-in-out

.playerLoader
  .preloader
    img
      height: 25px

a.links
  text-decoration: none
  color: black

.trash-btn
  margin-right: 15px
  display: inline-block
  transition: transform 0.2s ease-in-out
  cursor: pointer
  &:nth-of-type(3)
    margin-right: 15px
  &:hover
    transform: scale(1.2)
  &:active
    transform: scale(0.9)
</style>